import { ComponentType, useEffect, useRef, useState } from "react"
import { CapabilitiesResponseDataSchema } from "schema"
import z from "zod"
import { useCapabilityOverride } from "./useCapabilityOverride"

type CapabilityItem = z.infer<typeof CapabilitiesResponseDataSchema>[number]

type OverrideFormProps = {
  capability: CapabilityItem
  campaignId: string
  onClose: () => void
  onActionsChange?: (actions: OverrideFormActions) => void
}

type FieldsProps<T> = {
  value: T
  onChange: (value: T) => void
}

export type OverrideFormActions = {
  hasOverride: boolean
  isPending: boolean
  onSave: () => void
  onDelete: () => Promise<void>
}

export function createOverrideForm<T extends Record<string, unknown>>(
  FieldsComponent: ComponentType<FieldsProps<T>>,
  defaultValue: T,
  parseValue?: (raw: T) => T,
  getCapabilityDefault?: (capability: CapabilityItem) => Partial<T>,
) {
  return function OverrideForm({
    capability,
    campaignId,
    onClose,
    onActionsChange,
  }: OverrideFormProps) {
    const capabilityDefault = getCapabilityDefault?.(capability) ?? {}
    const effectiveDefaultValue = { ...defaultValue, ...capabilityDefault }
    const rawValue = {
      ...effectiveDefaultValue,
      ...((capability.campaignConfigOverride as Partial<T> | null) ?? {}),
    } as T
    const [value, setValue] = useState<T>(parseValue ? parseValue(rawValue) : rawValue)
    const valueRef = useRef(value)
    const effectiveDefaultValueRef = useRef(effectiveDefaultValue)
    valueRef.current = value
    effectiveDefaultValueRef.current = effectiveDefaultValue

    const { hasOverride, isPending, handleSave, handleDelete } = useCapabilityOverride({
      capability,
      campaignId,
      onClose,
    })

    useEffect(() => {
      onActionsChange?.({
        hasOverride,
        isPending,
        onSave: () => handleSave(valueRef.current as Record<string, unknown>),
        onDelete: async () => {
          await handleDelete()
          setValue(effectiveDefaultValueRef.current)
        },
      })
    }, [onActionsChange, hasOverride, isPending, handleSave, handleDelete])

    return <FieldsComponent value={value} onChange={setValue} />
  }
}
